/**
 * BYOK (Bring Your Own Key) settings for the parent PWA.
 *
 * The plaintext LLM API key never leaves the browser. On save:
 *   1. Envelope-encrypt the API key with the in-memory family key under a
 *      stable AAD (`"byok:v1"`).
 *   2. Persist the ciphertext bytes to IndexedDB under `BYOK_VAULT_KEY` so
 *      it survives page reloads on this device.
 *   3. Update `currentConfig.byok` with `{ provider, model, endpoint }` and
 *      call `save()` — the config PUT carries only the non-secret fields.
 *
 * The endpoint agent gets its family-key copy via the 012 keychain slot
 * (`keys::byok_api_key`) once the CP replicates the ciphertext there; that
 * cross-device delivery is out of scope for this module — we only prove the
 * plaintext never leaves the parent's browser.
 */

import { signal } from '@preact/signals';
import { encrypt } from '../e2ee/envelope';
import { familyKeyInMemory } from '../state/session';
import { api } from '../state/api';
import * as vault from '../e2ee/vault';
import { type ByokProvider } from './config';

/** Vault key under which the envelope-encrypted API key ciphertext lives. */
export const BYOK_VAULT_KEY = 'byok_api_key_ct';

/** AAD bound into envelope encryption for the BYOK ciphertext. */
export const BYOK_AAD = new TextEncoder().encode('byok:v1');

/**
 * Local echo of the last-saved BYOK provider settings (no secret material).
 * The route reads `currentConfig.value.byok` directly; this signal exists so
 * unit tests can observe save without wiring up the full config signal.
 */
export const lastSavedByok = signal<ByokProvider | null>(null);

/**
 * Persist BYOK provider settings.
 *
 * If `apiKey` is a non-empty string, encrypts + uploads a fresh ciphertext.
 * If it's the empty string (user left the field blank in the panel), we
 * SKIP the encrypt/upload and keep the existing ciphertext untouched — the
 * previously-saved key stays valid. Only the metadata (provider / model /
 * endpoint) is updated in the config PUT either way.
 *
 * Throws if the family key or current config is not loaded.
 */
export async function saveByok(
  settings: ByokProvider,
  apiKey: string,
): Promise<void> {
  const fk = familyKeyInMemory.value;
  if (fk === null) throw new Error('no family key in memory');

  // ONE request carries provider, model, endpoint and the encrypted key —
  // the shape plan 021 specified (docs/plans/021-pwa-byok-and-billing.md:46)
  // and the server writes in a single upsert.
  //
  // This matters because there is ONE key slot per family, shared by every
  // provider. When the two halves travelled separately, a key that landed
  // without its provider broke the provider that was working: endpoints kept
  // the old URL and started sending it the new key, so every capture 401'd
  // with nothing in the log or the UI to say why. Writing them together
  // makes that state unreachable rather than merely unlikely.
  const resolved: ByokProvider = {
    ...settings,
    endpoint:
      settings.provider === 'openai_compatible' && settings.endpoint !== null
        ? normalizeEndpointUrl(settings.endpoint)
        : settings.endpoint,
  };

  // The row always carries both halves, so a provider change with the key
  // field left blank must re-send the stored ciphertext rather than skip the
  // write — otherwise the row keeps the OLD provider and we are back to the
  // mismatch this whole shape exists to prevent.
  let ct: Uint8Array | null;
  if (apiKey.length > 0) {
    ct = await encrypt(fk, new TextEncoder().encode(apiKey), BYOK_AAD);
    await vault.put(BYOK_VAULT_KEY, ct);
  } else {
    // The vault is per-browser. A parent saving from a second browser, or
    // after clearing site data, has no copy to reuse — and skipping the
    // write there is silent: the config save still succeeds and the screen
    // reports success while the server keeps the OLD provider forever.
    // Refuse loudly instead. This is the whole failure class this file
    // exists to close, so it must not reappear at the last step.
    ct = await vault.get(BYOK_VAULT_KEY);
    if (ct === null) {
      throw new Error(
        'enter the API key — this browser has no saved copy to reuse. The ' +
          'key and the provider are written together, so the provider ' +
          'cannot be saved without it.',
      );
    }
  }

  await api('/v1/family/byok', {
    method: 'PUT',
    body: JSON.stringify({
      provider: resolved.provider,
      model: resolved.model,
      endpoint: resolved.endpoint,
      endpoint_append_path: resolved.endpoint_append_path ?? true,
      encrypted_api_key: Array.from(ct),
    }),
  });

  lastSavedByok.value = { ...resolved };
}

/** What `GET /v1/family/byok` returns. */
interface FamilyByokResponse {
  provider: string | null;
  model: string | null;
  endpoint: string | null;
  endpoint_append_path: boolean;
  has_key: boolean;
}

/** Whether the server holds an API key for this family. */
export async function fetchByokHasKey(): Promise<boolean> {
  const r = await api<Partial<FamilyByokResponse> | null>('/v1/family/byok');
  return r?.has_key === true;
}

/**
 * The provider settings the server holds, for the settings screen to show.
 *
 * `null` when the parent has never saved any. Read from the byok row rather
 * than the config document: the row is where the provider actually lives,
 * and reading it anywhere else is what let a stale copy drive an endpoint
 * for three days.
 */
export async function fetchByok(): Promise<ByokProvider | null> {
  const r = await api<Partial<FamilyByokResponse> | null>('/v1/family/byok');
  // Type-check rather than null-check. A field that is *missing* is not the
  // same as one that is null, and returning a half-formed object here hands
  // the caller `undefined.length`. Either we have a usable pair or we have
  // nothing.
  if (
    r === null ||
    typeof r.provider !== 'string' ||
    typeof r.model !== 'string'
  ) {
    return null;
  }
  return {
    provider: r.provider,
    model: r.model,
    endpoint: typeof r.endpoint === 'string' ? r.endpoint : null,
    endpoint_append_path: r.endpoint_append_path ?? true,
  };
}

/**
 * Canonicalise a self-hosted (OpenAI-compatible) endpoint URL for
 * storage: trim whitespace and trailing slashes.
 */
export function normalizeEndpointUrl(raw: string): string {
  return raw.trim().replace(/\/+$/, '');
}

/**
 * The exact URL a request will POST to. `appendPath` is the single
 * source of truth, persisted in the config (`endpoint_append_path`,
 * default `true`): when true the `/chat/completions` document path is
 * appended to a standard base URL (`http://localhost:11434/v1`); when
 * false the URL is the complete request target, used verbatim — for
 * gateways whose real path doesn't end in `/chat/completions` at all.
 * The URL is never inspected for its suffix. The settings screen
 * previews this live; the Rust endpoint agent applies the same flag.
 */
export function endpointRequestUrl(raw: string, appendPath: boolean): string {
  const base = normalizeEndpointUrl(raw);
  return appendPath ? `${base}/chat/completions` : base;
}

/**
 * AAD for a candidate key sent to the device for a one-shot test.
 *
 * Distinct from `BYOK_AAD` on purpose: the envelope bakes the AAD into key
 * derivation, so a ciphertext minted for a throwaway test simply will not
 * decrypt as the live key, nor the reverse. Must match
 * `crates/backstop-core/src/net/byok_test.rs::BYOK_TEST_AAD`.
 */
export const BYOK_TEST_AAD = new TextEncoder().encode('byok-test:v1');

/** What `GET /v1/family/byok/test/:id` returns. */
export interface ByokTestResult {
  /** `pending` until the device answers or the server times it out. */
  status: 'pending' | 'ok' | 'failed' | 'timeout';
  /** The provider's reply, on success. */
  reply: string | null;
  /** Human-readable failure, on failure. */
  error: string | null;
  /** Which device ran it, for the waiting and result copy. */
  device_label: string;
}

/**
 * Ask the monitored device to test `settings` with `apiKey`.
 *
 * Saves NOTHING. The candidate key is encrypted under the family key and
 * parked server-side as opaque bytes; only the device that runs the test can
 * read it, and the server destroys it the moment the test settles. The
 * family's live BYOK row is untouched, which is the whole point — the parent
 * is finding out whether a configuration works *before* rolling it.
 *
 * Deliberately does not touch the vault or `lastSavedByok`: a test that left
 * traces of an unsaved key would be a save wearing a different label.
 */
export async function requestByokTest(
  settings: ByokProvider,
  apiKey: string,
): Promise<{ test_id: string; device_label: string }> {
  const fk = familyKeyInMemory.value;
  if (fk === null) throw new Error('no family key in memory');
  if (apiKey.length === 0) throw new Error('enter an API key to test');
  const model = settings.model.trim();
  if (model.length === 0) throw new Error('pick a model first');

  const endpoint =
    settings.provider === 'openai_compatible'
      ? normalizeEndpointUrl(settings.endpoint ?? '')
      : settings.endpoint;
  if (
    settings.provider === 'openai_compatible' &&
    (endpoint ?? '').length === 0
  ) {
    throw new Error('enter the endpoint URL first');
  }

  const ct = await encrypt(fk, new TextEncoder().encode(apiKey), BYOK_TEST_AAD);
  return api('/v1/family/byok/test', {
    method: 'POST',
    body: JSON.stringify({
      provider: settings.provider,
      model,
      endpoint,
      endpoint_append_path: settings.endpoint_append_path ?? true,
      encrypted_api_key: Array.from(ct),
    }),
  });
}

/** Read the current state of a parked test. */
export async function fetchByokTest(testId: string): Promise<ByokTestResult> {
  return api(`/v1/family/byok/test/${testId}`);
}
